import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  fetchSmsCabinetCheck,
  resolveHeaderBalanceLine,
  type SmsCabinetCheckResponse,
} from '../../api/smsCabinetCheck';
import { prefetchSmsCascadeContext, getSmsCascadePrefetchCache } from '../../api/smsCascadePrefetch';
import { useTranslation } from 'react-i18next';
import type { SxProps, Theme } from '@mui/material/styles';

export type SmsHeaderBalanceProps = {
  sx?: SxProps<Theme>;
  /** Верхняя строка сумма (крупно), ниже подпись «Баланс» — как в макете шапки */
  variant?: 'inline' | 'stacked';
};

/**
 * Баланс из POST /sms/check и при необходимости из префетча GET /sms/create (поле countBalance).
 */
const SmsHeaderBalance: React.FC<SmsHeaderBalanceProps> = ({ sx, variant = 'inline' }) => {
  const { t } = useTranslation();
  const [line, setLine] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        await prefetchSmsCascadeContext();
      } catch {
        /* отдельные GET префетча уже глотают ошибки */
      }
      let check: SmsCabinetCheckResponse | null = null;
      try {
        check = await fetchSmsCabinetCheck();
      } catch {
        check = null;
      }
      if (cancelled) return;
      setLine(resolveHeaderBalanceLine(check, getSmsCascadePrefetchCache()));
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!line) return null;

  if (variant === 'stacked') {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          fontFamily: '"Montserrat", sans-serif',
          ...(sx ?? {}),
        }}
      >
        <Typography sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, lineHeight: '24px', fontWeight: 600, color: 'inherit' }}>
          {line}
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 12, lineHeight: '16px', fontWeight: 500, opacity: 0.92, color: 'inherit' }}>
          {t('layout.balanceLabel')}
        </Typography>
      </Box>
    );
  }

  return (
    <Typography
      component="span"
      variant="caption"
      sx={{ opacity: 0.92, whiteSpace: 'nowrap', ...(sx ?? {}) }}
    >
      {t('layout.balanceLine', { amount: line })}
    </Typography>
  );
};

export default SmsHeaderBalance;
